import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useUserData from "../hooks/useUserData";
import {  Grid,  Card,  CardContent,  Typography,  CircularProgress,  CardActionArea,  Dialog,  DialogTitle,  DialogContent,  DialogActions,  Button,  ButtonGroup,  List,  ListItem,  ListItemText,} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {  ResponsiveContainer,  PieChart,  Pie,  Cell,  Tooltip,  Legend,  BarChart,  Bar,  XAxis,  YAxis,  CartesianGrid,} from "recharts";
import { getCachedRoster } from "./RosterCache";

// Custom tooltip for charts
const CustomTooltip = ({ active, payload }) => {
  const theme = useTheme();
  if (active && payload && payload.length) {
    const { name, value } = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          padding: 8,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 4,
        }}
      >
        {`${name}: ${value.toLocaleString()}`}
      </div>
    );
  }
  return null;
};

const TeamProddy = () => {
  const { userData, loading } = useUserData();
  const navigate = useNavigate();
  const theme = useTheme();

  // state
  const [proddyData, setProddyData] = useState({});
  const [roster, setRoster] = useState([]);
  const [loadingProddy, setLoadingProddy] = useState(true);
  const [loadingRoster, setLoadingRoster] = useState(true);

  const [viewBy, setViewBy] = useState("resource");
  const [timeWindow, setTimeWindow] = useState("");
  const [openKey, setOpenKey] = useState(null);
  const [chartType, setChartType] = useState("pie");
  const [lastUpdated, setLastUpdated] = useState(null);

  // fetch & poll
  useEffect(() => {
    if (loading) return;
    if (!userData) return navigate("/starsonata");
    if (userData.teamId === "unassigned") return navigate("/join");
    if (!userData.verified) return navigate("/pending");

    let isMounted = true;
    setLoadingProddy(true);

    const fetchProd = () => {
      if (!isMounted) return;
      let url = `https://imp.slypandainteractive.com/proddy.php?team_id=${userData.teamId}`;
      if (timeWindow) url += `&window=${timeWindow}`;
      fetch(url)
        .then((res) => res.json())
        .then((data) => {
          if (isMounted) {
            setProddyData(data);
            setLastUpdated(new Date());
          }
        })
        .catch((err) => console.error("Error loading proddy data:", err))
        .finally(() => {
          if (isMounted) setLoadingProddy(false);
        });
    };

    fetchProd();
    const intervalId = setInterval(fetchProd, 30000);

    setLoadingRoster(true);
    getCachedRoster(userData.teamId)
      .then((r) => {
        if (isMounted) setRoster(r);
      })
      .finally(() => {
        if (isMounted) setLoadingRoster(false);
      });

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [loading, userData, navigate, timeWindow]);

  // flatten entries
  const flattenEntries = () => {
    const entries = [];
    const proc = (set) =>
      Object.entries(set).forEach(([resource, items]) => {
        // ignore any bogus entries with resource names over 50 chars
        if (resource.length > 50) {
          console.warn(`Skipping Dodgy Resource Name: ${resource}`);
          return;
        }
        items.forEach(({ bot_name, total_quantity }) => {
          entries.push({
            botName: bot_name,
            resource,
            quantity: Number(total_quantity),
          });
        });
      });

    if (proddyData.priority) proc(proddyData.priority);
    if (proddyData.others) proc(proddyData.others);

    // return the accumulated entries
    return entries;
  };

  // merge alts → mains
  const tableData = useMemo(() => {
    const merged = {};
    const altMap = {};
    roster.forEach(({ mainCharacterName, altCharacters }) =>
      altCharacters.forEach((alt) => {
        altMap[alt.toLowerCase()] = mainCharacterName;
      })
    );
    flattenEntries().forEach(({ botName, resource, quantity }) => {
      const main = altMap[botName.toLowerCase()] || botName;
      const key = `${main.toLowerCase()}::${resource.toLowerCase()}`;
      if (!merged[key]) merged[key] = { botName: main, resource, quantity: 0 };
      merged[key].quantity += quantity;
    });
    return Object.values(merged);
  }, [proddyData, roster]);

  // summaries
  const summaryByResource = useMemo(() => {
    const sums = {};
    tableData.forEach(({ resource, quantity }) => {
      sums[resource] = (sums[resource] || 0) + quantity;
    });
    return Object.entries(sums).map(([resource, total]) => ({
      key: resource,
      label: resource,
      total,
    }));
  }, [tableData]);

  const summaryByBot = useMemo(() => {
    const sums = {};
    tableData.forEach(({ botName, quantity }) => {
      sums[botName] = (sums[botName] || 0) + quantity;
    });
    return Object.entries(sums).map(([botName, total]) => ({
      key: botName,
      label: botName,
      total,
    }));
  }, [tableData]);

  // loading states
  if (loading || !userData) return <CircularProgress />;
  if (loadingProddy || loadingRoster)
    return <Typography align="center">Loading data…</Typography>;

  // pick items
  const items = viewBy === "resource" ? summaryByResource : summaryByBot;
  const COLORS = [
    theme.palette.primary.light,
    theme.palette.secondary.light,
    theme.palette.error.light,
    theme.palette.warning.light,
    theme.palette.success.light,
  ];

  // toggle styles
  const toggleStyles = (active) =>
    active
      ? {
          backgroundColor: "var(--theme-color)",
          color: "var(--theme-text-color)",
        }
      : {
          backgroundColor: "var(--theme-text-color)",
          color: "var(--theme-color)",
          borderColor: "var(--theme-color)",
        };

  // chart data
  const chartData = openKey
    ? tableData
        .filter((e) =>
          viewBy === "resource" ? e.resource === openKey : e.botName === openKey
        )
        .map((e) => ({
          name: viewBy === "resource" ? e.botName : e.resource,
          value: e.quantity,
        }))
    : [];

    const totalValue = chartData.reduce((sum, d) => sum + d.value, 0);
      // pie slice labels: show % instead of raw number
  const renderPercentLabel = ({ name, value }) => {
    if (!totalValue) return `${name}: 0%`;
    const pct = ((value / totalValue) * 100).toFixed(1);
    return `${name}: ${pct}%`;
  };

  return (
    <div>
      <Typography variant="h4" align="center" gutterBottom>
        Production Summary
      </Typography>

      <ButtonGroup sx={{ mb: 2 }}>
        <Button onClick={() => setViewBy("resource")} sx={toggleStyles(viewBy === "resource")}>
          By Resource
        </Button>
        <Button onClick={() => setViewBy("bot")} sx={toggleStyles(viewBy === "bot")}>
          By Player
        </Button>
      </ButtonGroup>

      <ButtonGroup sx={{ mb: 2, ml: 2 }}>
        {[
          { key: "", label: "All" },
          { key: "1h", label: "Last 1 h" },
          { key: "12h", label: "Last 12 h" },
          { key: "24h", label: "Last 24 h" },
          { key: "7d", label: "Last 7 days" },
          { key: "28d", label: "Last 28 days" },
        ].map(({ key, label }) => (
          <Button key={key || "all"} onClick={() => setTimeWindow(key)} sx={toggleStyles(timeWindow === key)}>
            {label}
          </Button>
        ))}
      </ButtonGroup>

      <Grid container spacing={2}>
        {items.map(({ key, label, total }) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
            <Card variant="outlined" sx={{ backgroundColor: "var(--theme-color)", color: "var(--theme-text-color)" }}>
              <CardActionArea
                onClick={() => {
                  setOpenKey(key);
                  setChartType("pie");
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {label}
                  </Typography>
                  <Typography variant="h5">{total.toLocaleString()}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {lastUpdated && (
        <Typography align="center" sx={{ mt: 2, fontSize: "1rem", color: "var(--theme-text-color)" }}>
          Last updated: {lastUpdated.toLocaleTimeString()}
        </Typography>
      )}

      <Dialog open={Boolean(openKey)} onClose={() => setOpenKey(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ textAlign: "center" }}>{openKey} Breakdown</DialogTitle>
        <DialogContent dividers>
          <ButtonGroup sx={{ mb: 2 }} size="small">
            <Button onClick={() => setChartType("pie")} sx={toggleStyles(chartType === "pie")}>
              Pie Chart
            </Button>
            <Button onClick={() => setChartType("bar")} sx={toggleStyles(chartType === "bar")}>
              Bar Chart
            </Button>
            <Button onClick={() => setChartType("list")} sx={toggleStyles(chartType === "list")}>
              List
            </Button>
          </ButtonGroup>

{chartType === "pie" && (
  <ResponsiveContainer width="100%" height={300}>
    <PieChart>
      <Pie
        data={chartData}
        dataKey="value"
        nameKey="name"
        cx="50%"
        cy="50%"
        outerRadius={100}
        label={renderPercentLabel}
      >
        {chartData.map((_, idx) => (
          <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
        ))}
      </Pie>
      <Tooltip content={<CustomTooltip />} />
      <Legend verticalAlign="bottom" height={36} />
    </PieChart>
  </ResponsiveContainer>
)}

          {chartType === "bar" && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" hide={viewBy === "resource"} tick={{ fill: "var(--theme-text-color)" }} />
                <YAxis tick={{ fill: "var(--theme-text-color)" }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value">
                  {chartData.map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}

          {chartType === "list" && (
            <List>
              {chartData.map(({ name, value }) => (
                <ListItem key={name} disableGutters sx={{ py: 1 }}>
                  <ListItemText
                    primary={name}
                    secondary={value.toLocaleString()}
                    primaryTypographyProps={{ fontWeight: "bold", color: "var(--theme-text-color)" }}
                    secondaryTypographyProps={{ color: "var(--theme-text-color)" }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenKey(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TeamProddy;
